import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { WxRequest as request } from 'wax-lib';
import dayjs from 'dayjs';
import { LocaleMessageObject } from 'vue-i18n';
import { VuetifyThemes } from 'vuetify/types/services/theme';

export interface Login {
    clientcode: string;
    username: string;
    password: string;
}

export interface Privilege {
    PRIVILEGE: string;
    DESCRIPTION: string;
}
export interface User {
    SESSIONGUID?: string;
}

export interface AppData {
    clientcode: string;
    menu: object[];
    title: string;
    initialLocale: LocaleMessageObject;
    logoutTimeout: number;
    themes: VuetifyThemes;
    logo: string;
    clientNotChoosable: boolean;
}

export interface InitData {
    app: AppData;
    user: User;
    login: string;
}

export interface LocaleData {
    locale: string;
    i18n: any;
}

export interface DbResourceItem {
    RESOURCEID: string;
    RESOURCESTRING: string;
}

function formatMessage(message: string) {
    const time = dayjs(Date.now()).format('HH:mm:ss');
    return `[${time}] ${message}`;
}

@Module // ({ namespaced: false, dynamic: false, name: 'user' })
export default class UserStore extends VuexModule {
    /** */
    private xUser: User = {};
    /** */
    private xPrivileges!: Privilege[];
    private xSessionid = '';
    private xMessages = [''];
    private xMessage = '';
    private xNoSessionMessage = '';
    private xClientcode = '';
    private xLogoutTimeout = 0;
    private xIdleTime = 0;
    private xTitle = '';
    private xThemes: VuetifyThemes | null = null;
    private xLogo: string | null = null;
    private xMenu = [{}];
    private xUnloadTime: Date | null = null;
    private xLocaleResource: LocaleMessageObject = {};
    private xDarkMode = 0;
    private xClientNotChoosable = false;

    public get sessionid() {
        return this.xSessionid;
    }

    public get isAuthenticated() {
        return !!this.sessionid;
    }

    public get user() {
        return this.xUser;
    }

    public get clientcode() {
        return this.xClientcode;
    }

    public get privilegeIds(): string[] {
        return this.xPrivileges ? this.xPrivileges.map((item: Privilege) => item.PRIVILEGE) : [];
    }

    public get privileges() {
        return this.xPrivileges;
    }

    public get lastMessage() {
        return this.xMessages[0];
    }

    public get currMessage() {
        return this.xMessage;
    }

    public get noSessionMessage() {
        return this.xNoSessionMessage;
    }

    public get idleTime() {
        return this.xIdleTime;
    }

    public get unloadTime() {
        return this.xUnloadTime;
    }

    public get logoutTimeout() {
        return this.xLogoutTimeout;
    }

    public get localeResource() {
        return this.xLocaleResource;
    }

    public get menu() {
        return this.xMenu;
    }

    public get darkMode() {
        return this.xDarkMode;
    }

    public get title() {
        return this.xTitle;
    }

    public get themes() {
        return this.xThemes;
    }

    public get logo() {
        return this.xLogo;
    }

    public get clientNotChoosable() {
        return this.xClientNotChoosable;
    }

    @Mutation
    public logout() {
        document.cookie = 'AUXSESSIONTOKEN=';
        this.xMessages.unshift(this.xMessage);
        this.xMessage = '';
        this.xUser = {};
        this.xPrivileges = [];
        this.xSessionid = '';
    }

    // INFO: https://gist.github.com/DawidMyslak/2b046cca5959427e8fb5c1da45ef7748
    // this.user = { ...this.user, SESSIONGUID: sessionid };
    @Mutation
    public updateSessionid(sessionid: string) {
        this.xSessionid = sessionid;
        this.xIdleTime = 0;
    }

    @Mutation
    public updateUser(user: User) {
        this.xUser = user;
    }

    @Mutation
    public updatePrivileges(privileges: Privilege[]) {
        this.xPrivileges = privileges;
    }

    @Mutation
    public initData(data: AppData) {
        this.xClientcode = data.clientcode;
        this.xMenu = data.menu;
        this.xTitle = data.title;
        this.xLogoutTimeout = data.logoutTimeout ? data.logoutTimeout * 60 : 0;
        this.xThemes = data.themes;
        this.xLogo = data.logo;
        this.xClientNotChoosable = data.clientNotChoosable;
    }

    @Mutation
    public error(message: string) {
        // TODO: mutations cannot access getters, refactor to actions ...
        if (this.xSessionid) {
            this.xMessage = formatMessage(message);
        } else {
            this.xNoSessionMessage = formatMessage(message);
        }
    }

    @Mutation
    public addMessage(message: string) {
        // TODO: mutations cannot access getters, refactor to actions ...
        if (this.xSessionid) {
            this.xMessages.unshift(formatMessage(message));
        }
    }

    @Mutation
    public clearMessage() {
        this.xMessages.unshift(this.xMessage);
        this.xMessage = '';
    }

    @Mutation
    public clearNoSessionMessage() {
        this.xNoSessionMessage = '';
    }

    @Mutation
    public unloadApp() {
        this.xUnloadTime = new Date();
        this.xIdleTime = 0;
    }

    @Mutation
    public updateLocaleResource(localeResource: LocaleMessageObject) {
        this.xLocaleResource = localeResource;
    }

    @Mutation
    public resetTimer() {
        this.xIdleTime = 0;
    }

    @Mutation
    public incrTimer() {
        this.xIdleTime += 1;
    }

    @Mutation public disableUnloadTime() {
        this.xUnloadTime = null;
    }

    @Mutation
    public updateDarkMode(val: number) {
        this.xDarkMode = val;
    }

    @Action
    public async loadInitialData(data: InitData) {
        this.context.commit('initData', data.app);
        if (!this.isAuthenticated && data.user && data.user.SESSIONGUID) {
            this.context.commit('updateSessionid', data.user.SESSIONGUID);
            await this.context.dispatch('loadUser');
            await this.context.dispatch('loadResources');
            await this.context.dispatch('loadPrivileges');
        }
    }

    @Action
    public async loadUser() {
        const res = await request().scalar('sesuserinfo', {});
        if (!res.data) return;

        this.context.commit('updateUser', res.data);
    }

    @Action
    public async loginUser(login: Login) {
        this.context.commit('logout');

        const res = await request().scalar('sesloginuser', {
            CLIENTCODE: login.clientcode,
            USERNAME: login.username,
            PASSWORD: login.password,
        });

        if (!res.data) {
            return res;
        }

        this.context.commit('updateSessionid', res.data.SESSIONGUID);
        this.context.commit('updateUser', res.data);
        await this.context.dispatch('loadResources');
        await this.context.dispatch('loadPrivileges');

        return res;
    }

    @Action
    public async loadPrivileges() {
        const res = await request().execute('sesselectprivilegenames');
        if (!res.data) return;
        this.context.commit('updatePrivileges', res.data);
    }

    @Action
    public async loadResources() {
        const res = await request().execute('sesselectresource');
        if (!res.data) return;
        const dbResourceItems: DbResourceItem[] = res.data;
        const subLocaleResources: any = {};
        for (const localeMessage of dbResourceItems) {
            subLocaleResources[localeMessage.RESOURCEID] = localeMessage.RESOURCESTRING;
        }
        this.context.commit('updateLocaleResource', subLocaleResources);
    }

    @Action
    public async logoutUser(redirect: boolean = true) {
        this.context.commit('logout');
        if (redirect) {
            window.location.href = '/';
        }
    }

    @Action
    public async lookupSession() {
        const data = await request().execute('seslookupsession');
        if (data.code !== 0) {
            await this.context.dispatch('logoutUser');
        }
    }

    @Action
    public async saveUser(user: User) {
        const data = await request().execute('sesupdateuser', user);
        if (data.code === 0) {
            this.context.commit('updateUser', user);
        }
    }

    @Action
    public async changePassword(data: any) {
        return await request().execute('sesupdatepassword', data);
    }
}
